import { Component, EventEmitter, Input, Output, forwardRef} from '@angular/core';
import {TableLazyLoadEvent, TableModule} from 'primeng/table';
import { DashboardBaseComponent } from '../shared/dashboard-base-component';
import {NgClass, SlicePipe} from '@angular/common';
import {ChartLoadingComponent} from "../chart-loading/chart-loading.component";
export type EtapaColumnType = 'dot' | 'text' | 'progress' | 'pill' | 'badge' | 'withSubtext';

interface EtapaColumnBase {
  header: string;
  field: string;
  type: EtapaColumnType;
  cssClass?: string;
  sortable?: boolean;
}

export interface EtapaColumnDot extends EtapaColumnBase {
  type: 'dot';
  /** Campo de la fila que trae el/los nombre(s) de clase CSS a aplicar (ej. 'gio-blue-bg') */
  rowClass: string;
}

export interface EtapaColumnText extends EtapaColumnBase {
  type: 'text';
  /** Campo de la fila que trae el/los nombre(s) de clase CSS a aplicar (ej. 'gio-blue-color')
   * Esto es util para declarar colores dinamicos
   */
  rowClass?: string;
}

export interface EtapaColumnProgress extends EtapaColumnBase {
  type: 'progress';
  /** Campo de la fila que trae el/los nombre(s) de clase CSS a aplicar (ej. 'gio-blue-bg') */
  rowClass: string;
  maxValue?: number;
  suffix?: string;
}

export interface EtapaColumnPill extends EtapaColumnBase {
  type: 'pill';
}

export interface EtapaColumnBadge extends EtapaColumnBase {
  type: 'badge';
  labelField: string;
  bgColorField: string;
  textColorField: string;
  /** Campo de la fila que trae el texto secundario (más pequeño, debajo del principal). Opcional */
  subtextField?: string;
}

export interface EtapaColumnWithSubtext extends EtapaColumnBase {
  type: 'withSubtext';
  /** Campo de la fila que trae el texto secundario (más pequeño, debajo del principal) */
  subtextField: string;
}

export type EtapaColumn = EtapaColumnDot | EtapaColumnText | EtapaColumnProgress | EtapaColumnPill | EtapaColumnBadge | EtapaColumnWithSubtext;

//isNew es para activar un estilo especial en la fila
export interface TableData {
  [key: string]: any;
  isNew?: boolean;
}

export interface TableTotal { [key: string]: string | number; }

export interface ResumenEtapaConfig {
  /** Título que aparece en el card-header */
  title?: string;
  /** Subtítulo debajo del título */
  sub?: string;
  /** Definición de columnas de la tabla */
  columns: EtapaColumn[];
  /** Filas de datos */
  data ?: TableData[];
  /** Fila de totales al pie de la tabla */
  total?: TableTotal;
  /** Etiqueta de la celda inicial en la fila total (por defecto 'TOTAL GENERAL') */
  totalLabel?: string;
  /** Muestra un badge con la cantidad de datos activos */
  showBadge?: boolean;
  /** Textos de las pills que aparecen en el header */
  pills?: string[];
  /** Activa paginación con N filas por página */
  pageSize?: number;
  /** Texto HTML del ticker scrolling mostrado entre header y tabla */
  ticker?: string;
}

@Component({
  selector: 'app-dashboard-table',
  templateUrl: './dashboard-table.component.html',
  styleUrls: ['./dashboard-table.component.scss'],
  imports: [SlicePipe, TableModule, ChartLoadingComponent, NgClass],
  standalone:true,
  providers: [
    {
      provide: DashboardBaseComponent,
      useExisting: forwardRef(() => DashboardTableComponent)
    }
  ]
})
export class DashboardTableComponent extends DashboardBaseComponent {
  isLoading = true;
  isLazyLoading = false;

  /** Activa paginación server-side: la tabla no pagína en memoria, emite (lazyLoad) y espera que el padre le pase la página pedida en config.data */
  @Input() lazy = false;
  /** Total de filas en el servidor (requerido cuando lazy = true, para que el paginador calcule las páginas) */
  @Input() totalRecords = 0;
  @Output() lazyLoad = new EventEmitter<TableLazyLoadEvent>();

  private _config!: ResumenEtapaConfig;

  @Input() set config(val: ResumenEtapaConfig) {
    this.isLazyLoading = false;
    this._config = val;
    if (val?.data) {
      this.isLoading = false;
      this.isLoaded = true;
    }
  }
  get config() { return this._config; }

  get data(): any[] {
    return this.config?.data || [];
  }

  get columns(){
    return this._config.columns;
  }
  barWidth(value: number, maxValue = 30): number {
    return Math.min((value / maxValue) * 100, 100);
  }

  asText(col: EtapaColumn): EtapaColumnText { return col as EtapaColumnText; }
  asDot(col: EtapaColumn): EtapaColumnDot { return col as EtapaColumnDot; }
  asProgress(col: EtapaColumn): EtapaColumnProgress { return col as EtapaColumnProgress; }
  asPill(col: EtapaColumn): EtapaColumnPill { return col as EtapaColumnPill; }
  asBadge(col: EtapaColumn): EtapaColumnBadge { return col as EtapaColumnBadge; }
  asWithSubtext(col: EtapaColumn): EtapaColumnWithSubtext { return col as EtapaColumnWithSubtext; }


  onTableLazyLoad($event: TableLazyLoadEvent) {
    this.isLazyLoading = true;
    this.lazyLoad.emit($event);
  }
}
